//==============================================================================
/**
@file       ESDIconPackParser.cpp

@brief      Parser for the icon-pack manifest.json

@author     Andy Fuchs

@copyright  (c) 2025, Elgato Systems GmbH. All Rights Reserved.

**/
//==============================================================================
/* eslint-disable */

import {dbgprintf} from './utils';
import {ESDIconPackC} from './ESDValidateIconPack';


const ICON_PACK_VERSION = "Version";
const ICON_PACK_NAME = "Name";
const ICON_PACK_AUTHOR = "Author";
const ICON_PACK_URL = "URL";
const ICON_PACK_ICON = "Icon";
const ICON_PACK_LICENSE = "License";

const ICON_METADATA_NAME = "name";
const ICON_METADATA_PATH = "path";
const ICON_METADATA_TAGS = "tags";

const SD_DISTRIBUTION_TOOL = true;

const kESDIconPackSupportedResult_Supported = 0;
const kESDIconPackSupportedResult_Malformed = 1;
const kESDIconPackSupportedResult_AlreadyInstalled = 2;

// const readFile = () => {
//     const reader = new FileReader();
//     reader.onloadend = (evt) => {
//         resolve(evt.target.result);
//     };
//     if(['blob', 'arraybuffer'].includes(responseType)) {
//         reader.readAsArrayBuffer(fileObj);
//     } else if(['text'].includes(responseType)) {
//         reader.readAsText(fileObj);
//     } else {
//         reader.readAsDataURL(fileObj);
//     }
// };

const createErrorMessage = (e) => {
    if(typeof e === 'object') {
        // if(e instanceof FileSystemFileEntry) {
        // if(e instanceof FileSystemEntry) {
        if(e.fullPath) {
            return e.fullPath.substr(1);
        } else if(e instanceof Error) {
            return `${e.name}: ${e.message}`;
        }
    }
    return e;
};

const fetchFile = (inFullPath) => {
    return new Promise(function(resolve, reject) {
        const request = Object.assign(new XMLHttpRequest());
        request.open('GET', inFullPath, true);
        request.onload = (e, f) => {
            if(request.responseType === 'json') {
                const jsn = Utils.parseJson(request.response);
                if(jsn) {
                    resolve(jsn);
                } else {
                    // dbg('[Error]::ReadFile.reply was not JSON', request);
                    reject('No JSON response!');
                }
            } else if(request.responseType === 'arraybuffer') {
                resolve(request.response);
            } else {
                resolve(request.responseText);
            }
        };
        request.send();
    });
};

function readFilePromise(file) {
    return new Promise(function(resolve) {
        const reader = new FileReader();
        reader.onloadend = function(evt) {
            resolve(evt.target.result);
        };
        reader.onerror = function() {
            resolve("");
        };
        reader.readAsText(file);
    });
};

export async function readFileEntry(entry, successCallback, errorCallback) {
    return new Promise(function(resolve) {
        entry.file(function(file) {
            let reader = new FileReader();

            reader.onload = function() {
                successCallback && successCallback(reader.result);
                resolve(reader.result);
            };

            reader.onerror = function() {
                errorCallback && errorCallback(reader.error);
                resolve('');
            };

            reader.readAsText(file);
        }, errorCallback);
    });
}

const fetchFileSync = (inFullPath) => {
    const request = new XMLHttpRequest();
    request.open('GET', decodeURIComponent(inFullPath), false);  // `false` makes the request synchronous
    request.send(null);
    return request.readyState === 4 ? request.responseText : 'NOTHING';
};

// #include "DebugPrint.hpp";
// EPL_DEBUG_INIT(ESDIconPackParser, EPL_DEBUG_DEFAULT_DBGPRINT_LEVEL);
class ESDIconPackParserC {
    constructor (options) {

    }
    async CreateIconPackFromPath(inUUID, inFullPath, inManifestPath, outSupportedResult = {result: 0}, inPathMulti) {

        try {
            // #if _UP_WINDOWS;
            //         // Fix for SD-2956 App Store: Can't install plugins if the username contains umlaut
            //         // which is similar to SD-1028
            //         USES_CONVERSION;
            //         std:: wstring wManifestPath = A2W_CP(inManifestPath.c_str(), CP_UTF8);

            //         std:: ifstream ifstream(wManifestPath, std:: ifstream::in);
            // #else;
            //         std:: ifstream ifstream(inManifestPath, std:: ifstream::in);
            // #endif;

            // Read the file
            // if Javascript

            const ifstream = typeof inManifestPath === 'object' ? await readFileEntry(inManifestPath) : fetchFileSync(inManifestPath);
            const manifestJson = JSON.parse(ifstream);

            //
            // Parse the icon-pack
            //
            const version = manifestJson[ICON_PACK_VERSION]; // Throw if missing
            const name = manifestJson[ICON_PACK_NAME]; // Throw if missing
            const author = manifestJson[ICON_PACK_AUTHOR]; // Throw if missing
            const icon = manifestJson[ICON_PACK_ICON]; // Throw if missing

            const url = manifestJson[ICON_PACK_URL] || ""; // Use a default value if the value does not exist
            const license = manifestJson[ICON_PACK_LICENSE] || ""; // Use a default value if the value does not exist

            outSupportedResult.result = kESDIconPackSupportedResult_Supported;
            return new ESDIconPackC(inUUID, version, name, author, url, icon, license, inFullPath, ifstream, manifestJson);
        }
        catch(error) {
            const errMsg = createErrorMessage(inManifestPath);
            dbgprintf("Error: Exception when parsing manifest file %s.  Error: %s", errMsg, createErrorMessage(error));
            console.trace("Exception when parsing manifest file %s", errMsg, error);
            outSupportedResult.result = kESDIconPackSupportedResult_Malformed;
            throw outSupportedResult;
        }

        return null;
    }
    async CreateIconsMetadataFromPath(inFullPath, outSupportedResult, inPathMulti) {
        let outIconsMetadata = [];
        // outSupportedResult = kESDIconPackSupportedResult_Supported;
        outSupportedResult.result = kESDIconPackSupportedResult_Supported;

        try {
            // #if _UP_WINDOWS;
            //         // Fix for SD-2956 App Store: Can't install plugins if the username contains umlaut
            //         // which is similar to SD-1028
            //         USES_CONVERSION;
            //         std:: wstring wIconsPath = A2W_CP(inFullPath.c_str(), CP_UTF8);

            //         std:: ifstream ifstream(wIconsPath, std:: ifstream::in);
            // #else;
            //         std:: ifstream ifstream(inFullPath, std:: ifstream::in);
            // #endif;

            // Read the file
            // const iconsJson = JSON.parse(ifstream);

            const ifstream = typeof inPathMulti === 'object' ? await readFileEntry(inPathMulti) : fetchFileSync(inFullPath);
            const iconsJson = JSON.parse(ifstream);

            //
            // Parse the icon-pack
            //
            if(Array.isArray(iconsJson)) {
                for(let iconMetadataJson of iconsJson) {
                    const path = iconMetadataJson[ICON_METADATA_PATH]; // Throw if missing
                    const name = iconMetadataJson[ICON_METADATA_NAME]; // Throw if missing

                    const tags = [];
                    const tagsJson = iconMetadataJson[ICON_METADATA_TAGS]; // Throw if missing

                    if(Array.isArray(tagsJson)) {
                        for(let j of tagsJson) {
                            if(j.length) tags.push(j);
                        }
                    } else {
                        if(SD_DISTRIBUTION_TOOL) {
                            dbgprintf("Error: The tags for '%s' is not a json array.", name);
                        }
                        outSupportedResult.result = kESDIconPackSupportedResult_Malformed;
                    }

                    const iconMetadata = {};
                    iconMetadata.name = name;
                    iconMetadata.path = path;
                    iconMetadata.tags = tags;

                    outIconsMetadata.push(iconMetadata);
                }
            }
        }
        catch(error) {
            dbgprintf("Exception when parsing the icons.json file %s. Error: %s", inFullPath, createErrorMessage(error));
            outSupportedResult.result = kESDIconPackSupportedResult_Malformed;
            throw outIconsMetadata;
        }

        return outIconsMetadata;
    }
}

export var ESDIconPackParser = new ESDIconPackParserC();
